import LimitedEditionIcon from "src/assets/all-icons/limited-edition-icon.jsx";

import AuctionTimeLeftIndicator from "@/pages/artwork/content-section/components/AuctionTimeLeftIndicator";
import { resolveLimitedEditionContext, resolveLimitedEditionSizeData } from "@/pages/artwork/content-section/utils/limitedEdition.utils";
import Text from "@/shared-components/text/components/Text";
import { getArtworkArtistDisplayName } from "@/utils/artworkArtists";
import { isRestrictedArtwork } from "@/utils/artworkVisibility";
import NumberUtils from "@/utils/number.utils";

import ArtworkImage from "./ArtworkImage";

import "./styles/ArtworkCard.css";

export default function ArtworkCard({
  artworkData,
  artistNameFallback = null,
  className = "",
  onClick,
  profileCollection = false,
  previewImageSize = 300,
  responsiveImageWidth = null,
  showAuctionMeta = false,
  limitedEditionSequenceNumber = null,
  limitedEditionSequenceTotal = null,
}) {
  if (!artworkData?.artwork_about) {
    const id = artworkData?._id ?? artworkData?.id ?? artworkData?.product_id ?? null;
    const holdingType =
      artworkData?.metadata?.holding_type
      ?? artworkData?.product_metadata?.holding_type
      ?? artworkData?.holding_type
      ?? null;
    console.warn("[ArtworkCard] Missing artwork_about; rendering with fallbacks.", {
      artworkId: id,
      holdingType,
      hasArtworkMedia: Boolean(artworkData?.artwork_media),
      keys: artworkData ? Object.keys(artworkData) : null,
    });
  }
  const {
    metadata: productMetadata,
    isLimitedEdition: isLimitedEditionProduct,
  } = resolveLimitedEditionContext(artworkData);
  const { data: limitedEditionSizeData } = resolveLimitedEditionSizeData(artworkData, {
    metadata: productMetadata,
  });
  const hasLimitedEditions =
    artworkData?.artwork_has_limited_editions === true
    || Boolean(artworkData?.artwork_limited_editions);
  const isProfileCollection = profileCollection === true;
  const isLimitedEditionPurchase = isLimitedEditionProduct;
  const isLimitedEditionHolding =
    (productMetadata?.holding_type ?? artworkData?.holding_type) === "limited_edition";
  const hasLimitedEditionForDisplay = hasLimitedEditions || isLimitedEditionPurchase;
  const shouldShowLimitedEditionIcon =
    hasLimitedEditionForDisplay && (!isProfileCollection || isLimitedEditionPurchase);

  const limitedEditionDetails =
    productMetadata?.limitedEdition
    ?? productMetadata?.limited_edition
    ?? artworkData?.metadata?.limitedEdition
    ?? artworkData?.metadata?.limited_edition
    ?? {};

  const limitedEditionNumber = limitedEditionDetails?.number ?? productMetadata?.edition_number ?? null;
  const limitedEditionEditionSize =
    limitedEditionDetails?.edition_size
    ?? productMetadata?.edition_size
    ?? limitedEditionDetails?.editionSize
    ?? null;
  const limitedEditionSizeLabel = (() => {
    const candidate =
      limitedEditionDetails?.size_label
      ?? limitedEditionDetails?.sizeLabel
      ?? limitedEditionDetails?.size_name
      ?? limitedEditionDetails?.sizeName
      ?? limitedEditionDetails?.size
      ?? null;
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
    return null;
  })();
  const adminOnlySizeLabel = (() => {
    const candidate =
      limitedEditionSizeData?.data?.size_label
      ?? limitedEditionSizeData?.data?.sizeLabel
      ?? limitedEditionSizeData?.data?.size
      ?? limitedEditionSizeData?.data?.name
      ?? limitedEditionSizeData?.sizeKey
      ?? limitedEditionSizeLabel
      ?? null;
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      const cleaned = candidate.trim().replace(/[_-]+/g, " ");
      return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
    }
    return null;
  })();

  const limitedEditionNumberText = (() => {
    if (!isLimitedEditionHolding) {
      return null;
    }
    if (
      isRestrictedArtwork(artworkData)
      && Number.isFinite(limitedEditionSequenceNumber)
      && Number.isFinite(limitedEditionSequenceTotal)
      && limitedEditionSequenceTotal > 0
    ) {
      if (adminOnlySizeLabel) {
        return `${adminOnlySizeLabel} ${limitedEditionSequenceNumber}/${limitedEditionSequenceTotal}`;
      }
      return `${limitedEditionSequenceNumber}/${limitedEditionSequenceTotal}`;
    }
    if (limitedEditionNumber && limitedEditionEditionSize) {
      return `${limitedEditionNumber}`;
    }
    if (limitedEditionNumber) {
      return `No. ${limitedEditionNumber}`;
    }
    if (limitedEditionEditionSize) {
      return `Edition of ${limitedEditionEditionSize}`;
    }
    return null;
  })();

  const isAuctionArtwork = Boolean(artworkData?.artwork_about?.artwork_is_auction);
  const auctionData = artworkData?.artwork_auction ?? {};
  const artworkCurrency =
    artworkData?.artwork_about?.artwork_currency ??
    auctionData?.currency ??
    "EUR";
  const currentBidPrice = auctionData?.current_bid_price ?? null;
  const auctionEndsAt = auctionData?.ends_at ?? null;
  const formattedCurrentBidPrice = (() => {
    const numericValue = Number(currentBidPrice);
    if (Number.isFinite(numericValue)) {
      return NumberUtils.getArtworkPrice(numericValue, artworkCurrency);
    }
    if (typeof currentBidPrice === "string" && currentBidPrice.trim()) {
      return `${currentBidPrice.trim()} ${artworkCurrency}`;
    }
    return null;
  })();

  const formattedArtworkPrice = (() => {
    const rawPrice = artworkData?.artwork_about?.artwork_price;
    if (typeof rawPrice === "number" && Number.isFinite(rawPrice)) {
      return NumberUtils.getArtworkPrice(rawPrice, artworkCurrency);
    }
    if (typeof rawPrice === "string" && rawPrice.trim().length > 0) {
      return rawPrice.trim();
    }
    return null;
  })();
  const shouldShowStandardPrice = !(showAuctionMeta && isAuctionArtwork) && Boolean(formattedArtworkPrice);
  const resolvedArtistDisplayName = getArtworkArtistDisplayName(artworkData);
  const artistDisplayName =
    (typeof resolvedArtistDisplayName === "string" && resolvedArtistDisplayName.trim().length > 0 && resolvedArtistDisplayName.trim() !== "-"
      ? resolvedArtistDisplayName.trim()
      : null)
    ?? (typeof artistNameFallback === "string" && artistNameFallback.trim().length > 0 ? artistNameFallback.trim() : null)
    ?? "-";
  const artworkDisplayTitle = artworkData?.artwork_about?.artwork_title ?? artworkData?.title ?? "-";
  const mainPicture = artworkData?.artwork_media?.artwork_main_picture ?? {};
  const artworkImageId = mainPicture?.image_id ?? artworkData?.image_id ?? null;
  const artworkAspectRatio = mainPicture?.image_original_data?.aspect_ratio ?? 1;
  const forceSmallerDimension = artworkAspectRatio > 1 ? "height" : "width";
  const hasResponsiveImageWidth = Number.isFinite(responsiveImageWidth) && responsiveImageWidth > 0;
  const imageRequestWidth = hasResponsiveImageWidth
    ? Math.min(1200, Math.max(400, Math.ceil(responsiveImageWidth * 2)))
    : previewImageSize;

  return (
    <div
      className={`artwork-card__card ${className} ${onClick ? "pointer" : ""}`}
      onClick={onClick}
    >
      <ArtworkImage
        artworkData={artworkData}
        image={artworkImageId}
        imageClassName="artwork-card__artwork-image"
        imageTemplateClassName="artwork-card__image-template"
        style={{ aspectRatio: artworkAspectRatio }}
        forceSmaller={typeof imageRequestWidth === "number" && imageRequestWidth > 0 ? imageRequestWidth : undefined}
        forceSmallerDimension={hasResponsiveImageWidth ? "width" : forceSmallerDimension}
        responsiveSizes={hasResponsiveImageWidth ? `${Math.ceil(responsiveImageWidth)}px` : undefined}
      />

      <div className="artwork-card__meta">
        <div className="artwork-card__title-row">
          <Text className="artwork-card__title" paragraph>
            {artworkDisplayTitle}
          </Text>

          {shouldShowLimitedEditionIcon && (
            <LimitedEditionIcon className="artwork-card__limited-edition-icon" />
          )}
        </div>

        <div className="artwork-card__details-row">
          <Text
            className="artwork-card__artist"
            paragraph>
            {artistDisplayName}
          </Text>
          {shouldShowStandardPrice ? (
            <Text
              className="artwork-card__price"
              paragraph>
              {formattedArtworkPrice}
            </Text>
          ) : null}
        </div>

        {profileCollection && isLimitedEditionHolding && (limitedEditionNumberText || limitedEditionSizeLabel) && (
          <div className="artwork-card__limited-edition-details">
            {limitedEditionNumberText && (
              <Text
                className="artwork-card__limited-edition-number"
                paragraph>
                {limitedEditionNumberText}
              </Text>
            )}
          </div>
        )}

      </div>

      {showAuctionMeta && isAuctionArtwork && (currentBidPrice || auctionEndsAt) && (
        <div className="artwork-card__auction-meta">
          {formattedCurrentBidPrice ? (
            <div className="artwork-card__auction-price" aria-label="Current bid">
              <span className="artwork-card__auction-price-value">{formattedCurrentBidPrice}</span>
            </div>
          ) : null}

          {auctionEndsAt ? (
            <AuctionTimeLeftIndicator
              endsAt={auctionEndsAt}
              className="artwork-card__auction-timer"
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
